import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { ChevronDown, ArrowRight, MapPin, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full h-14 bg-background border-2 border-zinc-700 px-4 font-body text-white placeholder:text-zinc-600 focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/40 transition-colors duration-150";

const Field = ({ label, name, value, onChange, type = "text", placeholder, required, testid }) => (
  <div>
    <label className="block font-heading text-xs uppercase tracking-widest text-zinc-400 mb-2">
      {label} {required && <span className="text-brand">*</span>}
    </label>
    <input
      className={inputCls}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      data-testid={testid}
    />
  </div>
);

export const AuditForm = ({ onSubmit, loading }) => {
  const [form, setForm] = useState({
    name: "", email: "", business_name: "", gbp_url: "", place_id: "",
    phone: "", website: "", city: "", category: "",
  });
  const [showMore, setShowMore] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const blurRef = useRef(null);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onBusinessChange = (e) => {
    const val = e.target.value;
    setForm((f) => ({ ...f, business_name: val, place_id: "" }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    setSearching(true);
    setOpen(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.post(`${API}/places/autocomplete`, { input: val });
        setSuggestions(data.suggestions || []);
      } catch {
        setSuggestions([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const selectSuggestion = (s) => {
    setForm((f) => ({
      ...f,
      business_name: s.main_text,
      place_id: s.place_id || "",
      city: s.secondary_text || f.city,
    }));
    setSuggestions([]);
    setOpen(false);
  };

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (blurRef.current) clearTimeout(blurRef.current);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <div className="bg-secondary border-2 border-zinc-700 p-7 sm:p-9 hard-shadow" data-testid="audit-form-card">
      <div className="mb-6">
        <span className="font-heading text-xs uppercase tracking-widest text-brand">No call required</span>
        <h2 className="font-display text-3xl sm:text-4xl uppercase tracking-tight text-white leading-none mt-2">
          Get your free audit
        </h2>
        <p className="font-body text-sm text-zinc-400 mt-3">
          Start typing your business name and pick it from the Google list — we'll pull the exact listing and tear it apart the way a competitor would.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5" data-testid="audit-form">
        <Field label="Your Name" name="name" value={form.name} onChange={handle} placeholder="Jordan Miller" required testid="input-name" />
        <Field label="Email" name="email" type="email" value={form.email} onChange={handle} placeholder="you@company.com" required testid="input-email" />

        {/* Business name with live Google autocomplete */}
        <div className="relative">
          <label className="block font-heading text-xs uppercase tracking-widest text-zinc-400 mb-2">
            Business Name <span className="text-brand">*</span>
          </label>
          <div className="relative">
            <input
              className={inputCls}
              type="text"
              name="business_name"
              value={form.business_name}
              onChange={onBusinessChange}
              onFocus={() => form.business_name.length >= 3 && suggestions.length > 0 && setOpen(true)}
              onBlur={() => { blurRef.current = setTimeout(() => setOpen(false), 180); }}
              placeholder="Start typing, e.g. Redline Moving"
              required
              autoComplete="off"
              data-testid="input-business-name"
            />
            {searching && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-4 w-4 text-brand animate-spin" />}
          </div>

          {open && (suggestions.length > 0 || searching) && (
            <ul className="absolute z-30 left-0 right-0 mt-1 bg-background border-2 border-zinc-700 max-h-72 overflow-y-auto shadow-xl" data-testid="business-suggestions">
              {searching && suggestions.length === 0 && (
                <li className="px-4 py-3 font-body text-sm text-zinc-500">Searching Google…</li>
              )}
              {suggestions.map((s, i) => (
                <li key={s.place_id || i}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-secondary transition-colors duration-100 border-b border-zinc-800 last:border-0"
                    data-testid={`suggestion-${i}`}
                  >
                    <MapPin className="h-4 w-4 text-brand mt-0.5 shrink-0" />
                    <span>
                      <span className="block font-body text-sm text-white">{s.main_text}</span>
                      {s.secondary_text && <span className="block font-body text-xs text-zinc-500">{s.secondary_text}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {form.place_id && (
            <p className="mt-2 font-body text-xs text-pass flex items-center gap-1" data-testid="place-selected">
              <CheckCircle2 className="h-3 w-3" /> Exact Google listing matched — your audit will be spot-on
            </p>
          )}
        </div>

        <Field label="Google Business Profile URL" name="gbp_url" value={form.gbp_url} onChange={handle} placeholder="https://maps.google.com/... (optional if picked above)" testid="input-gbp-url" />

        <button
          type="button"
          onClick={() => setShowMore(!showMore)}
          className="flex items-center gap-2 font-heading text-xs uppercase tracking-widest text-zinc-400 hover:text-brand transition-colors duration-150"
          data-testid="toggle-more-fields"
        >
          <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${showMore ? "rotate-180" : ""}`} />
          Add detail for a sharper audit
        </button>

        {showMore && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 fade-up" data-testid="more-fields">
            <Field label="Phone" name="phone" value={form.phone} onChange={handle} placeholder="(512) 555-0199" testid="input-phone" />
            <Field label="City / Service Area" name="city" value={form.city} onChange={handle} placeholder="Austin, TX" testid="input-city" />
            <Field label="Website" name="website" value={form.website} onChange={handle} placeholder="redlinemoving.com" testid="input-website" />
            <Field label="Primary Category" name="category" value={form.category} onChange={handle} placeholder="Moving company" testid="input-category" />
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="group w-full h-14 bg-brand hover:bg-brand-hover text-white font-heading text-lg uppercase tracking-wider flex items-center justify-center gap-3 transition-all duration-150 hover:-translate-y-1 hover:hard-shadow-white disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="audit-submit-btn"
        >
          {loading ? "Auditing..." : "Get my free audit"}
          {!loading && <ArrowRight className="h-5 w-5 transition-transform duration-150 group-hover:translate-x-1" />}
        </button>
        <p className="text-center font-body text-xs text-zinc-500">
          Instant, competitor-grade report on screen — plus an emailed copy.
        </p>
      </form>
    </div>
  );
};

export default AuditForm;
